const RESERVED_RECHARTS_DATA_KEYS = new Set(['ref', 'key', 'children'])
const INTERNAL_DATA_KEY_PREFIX = '__chart_'

export type RechartsDataRow = Record<string, unknown>

interface NormalizedRechartsData<T extends RechartsDataRow> {
  data: T[]
  mapDataKey: (dataKey: string) => string
}

function getSafeDataKey(sourceKey: string, usedKeys: Set<string>): string {
  let safeKey = `${INTERNAL_DATA_KEY_PREFIX}${sourceKey}`

  while (usedKeys.has(safeKey)) {
    safeKey = `${safeKey}_`
  }

  return safeKey
}

/**
 * Removes React-reserved data fields before rows are passed to Recharts.
 *
 * Recharts spreads data rows into the props of its internal shape components.
 * A numeric `ref` field therefore becomes an invalid React ref. The original
 * key is mapped to an internal, non-reserved key so dataKey-based rendering
 * continues to work without changing the API response.
 */
export function normalizeRechartsData<T extends RechartsDataRow>(
  data: readonly T[],
  referencedKeys: readonly string[] = []
): NormalizedRechartsData<T> {
  const sourceKeys = new Set([...referencedKeys, ...data.flatMap((row) => Object.keys(row))])
  const usedKeys = new Set(sourceKeys)
  const keyMap = new Map<string, string>()

  for (const sourceKey of sourceKeys) {
    if (!RESERVED_RECHARTS_DATA_KEYS.has(sourceKey)) {
      continue
    }

    const safeKey = getSafeDataKey(sourceKey, usedKeys)
    keyMap.set(sourceKey, safeKey)
    usedKeys.add(safeKey)
  }

  const normalizedData = data.map((row) => {
    const normalizedRow: RechartsDataRow = { ...row }

    for (const [sourceKey, safeKey] of keyMap) {
      if (Object.prototype.hasOwnProperty.call(row, sourceKey)) {
        normalizedRow[safeKey] = row[sourceKey]
        delete normalizedRow[sourceKey]
      }
    }

    return normalizedRow as T
  })

  return {
    data: normalizedData,
    mapDataKey: (dataKey) => keyMap.get(dataKey) ?? dataKey,
  }
}

export function remapRechartsRecordKeys<T>(
  record: Record<string, T>,
  mapDataKey: (dataKey: string) => string
): Record<string, T> {
  return Object.fromEntries(Object.entries(record).map(([dataKey, value]) => [mapDataKey(dataKey), value]))
}
